import { Component } from '@theme/component';

/**
 * Pincode -> expected delivery date.
 *
 * The delivery API is optional and deliberately kept behind an app proxy: Shiprocket
 * authenticates with a Bearer token, so calling it directly from the storefront would
 * publish that token to every visitor. When `data-api-endpoint` is absent, or the call
 * fails, or the payload has no usable date, the component falls back to today plus
 * `data-fallback-days` so the field always answers.
 *
 * @extends {Component}
 */
class DeliveryEstimate extends Component {
  requiredRefs = ['input', 'button', 'result'];

  /** Guards against overlapping checks when the button is hit repeatedly. */
  #pending = false;

  handleInput() {
    // Keep the field numeric without fighting the caret: strip anything else as typed.
    const input = this.#input;
    if (!input) return;

    const digitsOnly = input.value.replace(/\D/g, '');
    if (digitsOnly !== input.value) input.value = digitsOnly;

    this.#hideResult();
  }

  /** @param {KeyboardEvent} event */
  handleKeydown(event) {
    if (event.key !== 'Enter') return;
    // The block can sit next to a product form; never let Enter submit anything.
    event.preventDefault();
    this.check();
  }

  async check() {
    if (this.#pending) return;

    const input = this.#input;
    if (!input) return;

    const pincode = input.value.trim();
    const expectedLength = Number(this.dataset.pincodeLength) || 6;

    if (!new RegExp(`^\\d{${expectedLength}}$`).test(pincode)) {
      this.#showResult(this.dataset.messageInvalid || 'Please enter a valid pincode.', 'error');
      input.focus();
      return;
    }

    this.#pending = true;
    this.#setBusy(true);
    this.#showResult(this.dataset.messageChecking || 'Checking…', 'pending');

    try {
      const result = await this.#lookup(pincode);

      if (result?.serviceable === false) {
        this.#showResult(this.dataset.messageUnserviceable || "We don't deliver to this pincode yet.", 'error');
        return;
      }

      const date = result?.date ?? this.#fallbackDate();
      const template = this.dataset.messageSuccess || 'Expected delivery by [date]';
      this.#showResult(template.replace('[date]', this.#formatDate(date)), 'success');
    } finally {
      this.#pending = false;
      this.#setBusy(false);
    }
  }

  /**
   * Queries the configured proxy.
   *
   * @param {string} pincode
   * @returns {Promise<{date?: Date, serviceable?: boolean} | null>}
   */
  async #lookup(pincode) {
    const endpoint = this.dataset.apiEndpoint;
    if (!endpoint) return null;

    const params = new URLSearchParams({ pincode });
    if (this.dataset.productId) params.set('product_id', this.dataset.productId);
    if (this.dataset.variantId) params.set('variant_id', this.dataset.variantId);

    try {
      const separator = endpoint.includes('?') ? '&' : '?';
      const response = await fetch(`${endpoint}${separator}${params}`, {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) return null;

      const data = await response.json();

      if (data?.serviceable === false) return { serviceable: false };

      if (typeof data?.estimated_date === 'string') {
        const parsed = new Date(data.estimated_date);
        if (!Number.isNaN(parsed.getTime())) return { date: parsed };
      }

      if (Number.isFinite(data?.days)) {
        return { date: this.#daysFromNow(Number(data.days)) };
      }

      return null;
    } catch {
      // Network or CORS failure — the caller substitutes the fallback date.
      return null;
    }
  }

  #fallbackDate() {
    return this.#daysFromNow(Number(this.dataset.fallbackDays) || 7);
  }

  /** @param {number} days */
  #daysFromNow(days) {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date;
  }

  /** @param {Date} date */
  #formatDate(date) {
    try {
      return new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      }).format(date);
    } catch {
      return date.toDateString();
    }
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    const button = this.refs.button;
    if (button instanceof HTMLButtonElement) button.disabled = busy;
  }

  /**
   * @param {string} message
   * @param {'success' | 'error' | 'pending'} state
   */
  #showResult(message, state) {
    const result = this.refs.result;
    if (!(result instanceof HTMLElement)) return;

    result.textContent = message;
    result.dataset.state = state;
    result.hidden = false;
  }

  #hideResult() {
    const result = this.refs.result;
    if (!(result instanceof HTMLElement)) return;

    result.hidden = true;
    result.textContent = '';
  }

  get #input() {
    const input = this.refs.input;
    return input instanceof HTMLInputElement ? input : null;
  }
}

if (!customElements.get('delivery-estimate-component')) {
  customElements.define('delivery-estimate-component', DeliveryEstimate);
}
